import { AUTOMATIONS_ICON } from '#pplx/frontend/apps/ask/automations-shared/constants.js';
import { getExternalUrlLabel } from '#pplx/frontend/apps/ask/canvas-shared/externalUrl.js';
import { getIconForFileType, getTextForFileType, } from '#pplx/frontend/apps/ask/canvas-shared/utils/fileTypeUtils.js';
import { getFileExtension } from '#pplx/frontend/apps/ask/files/file-utils/fileUtils.js';
import { useSessionWorkersSnapshot } from '#pplx/frontend/apps/ask/shared/SessionWorkersContextShared.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { useIntl } from 'react-intl';
import { match } from 'ts-pattern';
function firstNonEmptyString(...values) {
    return values.map((value) => value?.trim()).find(Boolean) ?? null;
}
function getFilePathBasename(asset) {
    const filePath = asset?.file_path?.trim();
    if (!filePath)
        return null;
    return filePath.split(/[\\/]/).filter(Boolean).pop() ?? null;
}
export const getAssetDownloadFilename = (asset) => {
    return (firstNonEmptyString(asset?.fallback_info?.name, asset?.download_info?.[0]?.filename, getFilePathBasename(asset)) ?? undefined);
};
/** Localized title for a GENERATED_IMAGE asset based on its media sub-type. */
export function getLocalizedImageTitle(mediaType, $t) {
    if (mediaType === 'MEDIA_TYPE_CHART')
        return $t({
            defaultMessage: 'Chart',
            description: 'Title for a chart image asset',
            id: 'e85aa54798',
        });
    if (mediaType === 'MEDIA_TYPE_AI')
        return $t({
            defaultMessage: 'Generated Image',
            description: 'Title for an AI-generated image asset',
            id: 'b358897a86',
        });
    return $t({
        defaultMessage: 'Image',
        description: 'Title for an image asset',
        id: '047a4daae7',
    });
}
/** Localized title for a GENERATED_VIDEO asset based on its media sub-type. */
export function getLocalizedVideoTitle(mediaType, $t) {
    if (mediaType === 'MEDIA_TYPE_AI')
        return $t({
            defaultMessage: 'Generated Video',
            description: 'Title for an AI-generated video asset',
            id: '7818817344',
        });
    return $t({
        defaultMessage: 'Video',
        description: 'Title for a video asset',
        id: 'ed352d25ed',
    });
}
function isThumbnailPreviewType(type) {
    return type?.trim().toLowerCase() === 'thumbnail';
}
function getLocalizedCodeFileDescription(extension, $t) {
    switch (extension) {
        case 'py':
            return $t({
                defaultMessage: 'Python',
                id: '5cfab3decc',
                description: 'File-type label for a Python file in an asset row or chip',
            });
        case 'js':
            return $t({
                defaultMessage: 'JavaScript',
                id: '8200acd3a0',
                description: 'File-type label for a JavaScript file in an asset row or chip',
            });
        case 'ts':
            return $t({
                defaultMessage: 'TypeScript',
                id: '6e2c25bbac',
                description: 'File-type label for a TypeScript file in an asset row or chip',
            });
        case 'jsx':
        case 'tsx':
            return $t({
                defaultMessage: 'React',
                id: '43f32a5e84',
                description: 'File-type label for a React JSX or TSX file in an asset row or chip',
            });
        case 'json':
            return $t({
                defaultMessage: 'JSON',
                id: 'b6649565e1',
                description: 'File-type label for a JSON data file in an asset row or chip',
            });
        case 'csv':
            return $t({
                defaultMessage: 'CSV',
                id: '5f55ac2c5f',
                description: 'File-type label for a comma-separated values file in an asset row or chip',
            });
        case 'md':
            return $t({
                defaultMessage: 'Markdown',
                id: 'ecaccaca8f',
                description: 'File-type label for a Markdown document in an asset row or chip',
            });
        case 'txt':
            return $t({
                defaultMessage: 'Text',
                id: '832c77d855',
                description: 'File-type label for a plain text file in an asset row or chip',
            });
        case 'zip':
            return $t({
                defaultMessage: 'ZIP',
                id: '1f211957ff',
                description: 'File-type label for a ZIP archive in an asset row or chip',
            });
        case 'html':
            return $t({
                defaultMessage: 'HTML',
                id: '03fb03fb52',
                description: 'File-type label for an HTML document in an asset row or chip',
            });
        case 'css':
            return $t({
                defaultMessage: 'CSS',
                id: '7909c69775',
                description: 'File-type label for a CSS stylesheet in an asset row or chip',
            });
        case 'sql':
            return $t({
                defaultMessage: 'SQL',
                id: 'a20f526e9d',
                description: 'File-type label for a SQL file in an asset row or chip',
            });
        case 'sh':
            return $t({
                defaultMessage: 'Shell Script',
                id: 'e5d110ad65',
                description: 'File-type label for a shell script in an asset row or chip',
            });
        case 'yaml':
        case 'yml':
            return $t({
                defaultMessage: 'YAML',
                id: '39cd074441',
                description: 'File-type label for a YAML configuration file in an asset row or chip',
            });
        case 'xml':
            return $t({
                defaultMessage: 'XML',
                id: 'b5af49168f',
                description: 'File-type label for an XML document in an asset row or chip',
            });
        case 'rb':
            return $t({
                defaultMessage: 'Ruby',
                id: '6223b17326',
                description: 'File-type label for a Ruby file in an asset row or chip',
            });
        case 'go':
            return $t({
                defaultMessage: 'Go',
                id: 'b89e4776a4',
                description: 'File-type label for a Go programming language file in an asset row or chip',
            });
        case 'rs':
            return $t({
                defaultMessage: 'Rust',
                id: '2c809df8c5',
                description: 'File-type label for a Rust file in an asset row or chip',
            });
        case 'java':
            return $t({
                defaultMessage: 'Java',
                id: '4bde158d4a',
                description: 'File-type label for a Java file in an asset row or chip',
            });
        case 'c':
            return $t({
                defaultMessage: 'C',
                id: '84ba93b4f7',
                description: 'File-type label for a C programming language file in an asset row or chip',
            });
        case 'cpp':
            return $t({
                defaultMessage: 'C++',
                id: '126cd6dd98',
                description: 'File-type label for a C++ file in an asset row or chip',
            });
        case 'swift':
            return $t({
                defaultMessage: 'Swift',
                id: '803dbb58db',
                description: 'File-type label for a Swift file in an asset row or chip',
            });
        case 'kt':
            return $t({
                defaultMessage: 'Kotlin',
                id: 'f8990df972',
                description: 'File-type label for a Kotlin file in an asset row or chip',
            });
        case 'r':
            return $t({
                defaultMessage: 'R',
                id: '7288925b53',
                description: 'File-type label for an R programming language file in an asset row or chip',
            });
        default:
            return undefined;
    }
}
export const isDownloadOnlyUnknownAsset = (asset) => {
    if (asset?.asset_type !== 'UNKNOWN') {
        return false;
    }
    return (asset.app == null &&
        asset.pdf_file == null &&
        asset.docx_file == null &&
        asset.doc_file == null &&
        asset.xlsx_file == null &&
        asset.code_file == null &&
        asset.chart == null &&
        asset.quiz == null &&
        asset.flashcards == null &&
        asset.research_report == null &&
        asset.audio_file == null &&
        asset.model_3d == null &&
        asset.visualization == null &&
        asset.slide_file == null &&
        asset.generated_image == null &&
        asset.generated_video == null &&
        asset.screenshot == null &&
        !!(asset.download_info?.[0]?.url || asset.fallback_info?.webview_url));
};
/**
 * Resolves a Teammate worker asset's display title: the stored title when
 * present, otherwise the live worker name from the session workers snapshot.
 * Historical assets may have been persisted with an empty title.
 */
export function useTeammateWorkerAssetTitle(asset) {
    const { workers } = useSessionWorkersSnapshot();
    if (asset?.asset_type !== 'TEAMMATE_WORKER')
        return undefined;
    const storedTitle = asset.teammate_worker?.title?.trim();
    if (storedTitle)
        return storedTitle;
    const workerId = asset.teammate_worker?.worker_id?.trim();
    if (!workerId)
        return undefined;
    return workers.find(({ contextUuid }) => contextUuid === workerId)?.name?.trim() || undefined;
}
export const getAssetTitle = (asset) => {
    switch (asset?.asset_type) {
        case 'CODE_ASSET':
            return 'Python';
        case 'CHART':
            return asset.chart?.name ?? 'Chart';
        case 'CODE_FILE':
            return asset.code_file?.name ?? asset.code_file?.filename ?? 'Code File';
        case 'APP':
            return asset.app?.name ?? 'App';
        case 'SLIDES':
            return asset.slide_file?.name ?? asset.app?.name ?? 'Slides';
        case 'PDF_FILE':
            return asset.pdf_file?.name ?? 'PDF';
        case 'DOCX_FILE':
            return asset.docx_file?.name ?? 'Document';
        case 'DOC_FILE':
            return asset.doc_file?.name ?? 'Document';
        case 'XLSX_FILE':
            return asset.xlsx_file?.name ?? 'Spreadsheet';
        case 'QUIZ':
            return asset.quiz?.title ?? 'Quiz';
        case 'FLASHCARDS':
            return asset.flashcards?.title ?? 'Flashcards';
        case 'GENERATED_IMAGE': {
            const filename = getAssetDownloadFilename(asset);
            if (filename)
                return filename;
            return asset.generated_image?.type === 'MEDIA_TYPE_CHART'
                ? 'Chart'
                : asset.generated_image?.type === 'MEDIA_TYPE_AI'
                    ? 'Generated Image'
                    : 'Image';
        }
        case 'GENERATED_VIDEO':
            return (getAssetDownloadFilename(asset) ??
                (asset.generated_video?.type === 'MEDIA_TYPE_AI' ? 'Generated Video' : 'Video'));
        case 'AUDIO_FILE':
            return asset.audio_file?.name ?? 'Audio';
        case 'MODEL_3D':
            return asset.model_3d?.name ?? asset.model_3d?.filename ?? '3D Model';
        case 'RESEARCH_REPORT':
            return asset.research_report?.name ?? 'Research Report';
        case 'EXTERNAL_URL':
            return getExternalUrlLabel(asset.external_url?.url, asset.external_url?.title);
        case 'AUTOMATION':
            return asset.automation?.title?.trim() || 'Automation';
        case 'PULL_REQUEST':
            return asset.pull_request?.title?.trim() || 'Pull request';
        case 'SESSION':
            return asset.session?.title?.trim() || 'Session';
        case 'TEAMMATE_WORKER':
            return asset.teammate_worker?.title?.trim() || 'Agent';
        case 'VISUALIZATION':
            return asset.visualization?.name ?? 'Visualization';
        default:
            return getAssetDownloadFilename(asset) ?? 'Asset';
    }
};
/** Canonical icon for each asset type. Shared by canvas and library code. */
export function getAssetTypeIcon(assetType) {
    switch (assetType) {
        case 'CODE_ASSET':
            return $icon('code');
        case 'ASSET_DIFF':
            return $icon('code');
        case 'CHART':
            return $icon('chart-histogram');
        case 'CODE_FILE':
            return $icon('file');
        case 'APP':
            return $icon('layout-collage');
        case 'SLIDES':
            return $icon('presentation');
        case 'PDF_FILE':
            return $icon('file-type-pdf');
        case 'DOCX_FILE':
            return $icon('file-type-docx');
        case 'DOC_FILE':
            return $icon('file-text');
        case 'XLSX_FILE':
            return $icon('file-spreadsheet');
        case 'GENERATED_IMAGE':
            return $icon('image-in-picture');
        case 'GENERATED_VIDEO':
            return $icon('video');
        case 'AUDIO_FILE':
            return $icon('file-music');
        case 'MODEL_3D':
            return $icon('box');
        case 'RESEARCH_REPORT':
            return $icon('file-text');
        case 'QUIZ':
            return $icon('checklist');
        case 'FLASHCARDS':
            return $icon('cards');
        case 'SCREENSHOT':
            return $icon('camera');
        case 'EXTERNAL_URL':
            return $icon('external-link');
        case 'AUTOMATION':
            return AUTOMATIONS_ICON;
        case 'PULL_REQUEST':
            return $icon('brand-github');
        case 'SESSION':
            return $icon('messages');
        case 'TEAMMATE_WORKER':
            return $icon('code');
        case 'VISUALIZATION':
            return $icon('layout-collage');
        default:
            return $icon('file');
    }
}
function getIconFromFilename(filename) {
    const extension = getFileExtension(filename);
    switch (extension) {
        case 'py':
            return $icon('brand-python');
        case 'js':
            return $icon('brand-javascript');
        case 'ts':
            return $icon('brand-typescript');
        case 'jsx':
        case 'tsx':
            return $icon('brand-react');
        default:
            return undefined;
    }
}
function getFilename(asset) {
    const filename = getAssetDownloadFilename(asset) ?? '';
    switch (asset?.asset_type) {
        case 'CODE_ASSET':
            return asset?.code_file?.filename ?? filename;
        case 'CHART':
            return asset?.chart?.name ?? filename;
        case 'CODE_FILE':
            return asset?.code_file?.filename ?? filename;
        case 'ASSET_DIFF':
            return asset?.asset_diff?.title ?? filename;
        case 'APP':
            return asset?.app?.name ?? filename;
        case 'SLIDES':
            return asset?.slide_file?.name ?? asset?.app?.name ?? filename;
        case 'PDF_FILE':
            return asset?.pdf_file?.name ?? filename;
        case 'DOCX_FILE':
            return asset?.docx_file?.name ?? filename;
        case 'DOC_FILE':
            return asset?.doc_file?.name ?? filename;
        case 'XLSX_FILE':
            return asset?.xlsx_file?.name ?? filename;
        case 'QUIZ':
            return asset?.quiz?.title ?? filename;
        case 'FLASHCARDS':
            return asset?.flashcards?.title ?? filename;
        case 'AUDIO_FILE':
            return asset?.audio_file?.filename ?? asset?.audio_file?.name ?? filename;
        case 'MODEL_3D':
            return asset?.model_3d?.filename ?? asset?.model_3d?.name ?? filename;
        case 'RESEARCH_REPORT':
            return asset?.research_report?.name ?? filename;
        default:
            return filename;
    }
}
function getAssetPreviewInfoEntries(asset) {
    return asset?.preview_info ?? [];
}
function getPreviewInfoUrl(preview) {
    return preview.s3_url ?? undefined;
}
export function getFirstAssetPreviewInfoUrl(asset) {
    return getAssetPreviewInfoEntries(asset).map(getPreviewInfoUrl).find(Boolean);
}
export function getAssetThumbnailPreviewUrl(asset) {
    const thumbnailPreview = getAssetPreviewInfoEntries(asset).find((preview) => isThumbnailPreviewType(preview.type) && getPreviewInfoUrl(preview));
    return thumbnailPreview ? getPreviewInfoUrl(thumbnailPreview) : undefined;
}
export function getAssetDisplayPreviewUrl(asset) {
    const thumbnailPreviewUrl = getAssetThumbnailPreviewUrl(asset);
    if (thumbnailPreviewUrl) {
        return thumbnailPreviewUrl;
    }
    switch (asset?.asset_type) {
        case 'GENERATED_IMAGE':
            return asset.generated_image?.url ?? getFirstAssetPreviewInfoUrl(asset);
        case 'SCREENSHOT':
            return asset.screenshot?.url ?? getFirstAssetPreviewInfoUrl(asset);
        case 'APP':
            return asset.app?.preview_url ?? getFirstAssetPreviewInfoUrl(asset);
        default:
            return getFirstAssetPreviewInfoUrl(asset);
    }
}
/**
 * Returns the largest available preview URL from the asset's preview_info,
 * sorted by pixel area (width * height). Use this when the asset is
 * rendered at a large display size (e.g. full-width or justified row) so
 * that the preview image is not blurry.
 */
export function getAssetLargestPreviewUrl(asset) {
    const entries = getAssetPreviewInfoEntries(asset).filter((preview) => !!getPreviewInfoUrl(preview));
    if (entries.length === 0)
        return undefined;
    const largest = entries.reduce((best, current) => {
        const bestArea = (best.width ?? 0) * (best.height ?? 0);
        const currentArea = (current.width ?? 0) * (current.height ?? 0);
        return currentArea > bestArea ? current : best;
    });
    return getPreviewInfoUrl(largest);
}
/**
 * Best-available preview image for a thread Asset. Prefers the full-size
 * server render over the smaller thumbnail transform, which can carry a dead
 * signature; then falls back to type-specific and fallback image fields.
 */
export function getAssetCardPreviewImageUrl(asset) {
    return firstNonEmptyString(getAssetLargestPreviewUrl(asset), getAssetDisplayPreviewUrl(asset), asset.doc_file?.preview_url, asset.xlsx_file?.preview_url, asset.fallback_info?.preview_image_url);
}
export const useAssetInfo = (asset) => {
    const { $t } = useIntl();
    const teammateWorkerTitle = useTeammateWorkerAssetTitle(asset);
    const filename = getFilename(asset);
    const extension = getFileExtension(filename);
    const previewUrl = getAssetDisplayPreviewUrl(asset);
    const mediaType = asset?.generated_image?.type ?? asset?.generated_video?.type;
    const typeDescription = useAssetTypeDescription(asset?.asset_type, mediaType);
    const inferredFileTypeText = asset?.asset_type != null ? getTextForFileType(asset.asset_type, extension) : '';
    const defaults = {
        description: typeDescription || inferredFileTypeText,
        title: '',
        filename,
        Icon: getAssetTypeIcon(asset?.asset_type),
        isStreaming: false,
        previewUrl,
    };
    const baseInfo = match(asset?.asset_type)
        .with('CODE_ASSET', () => ({
        ...defaults,
        title: $t({
            defaultMessage: 'Python',
            description: 'Title for a Python code asset in the chain-of-thought',
            id: '61bb1c0d84',
        }),
        Icon: $icon('code'),
    }))
        .with('CHART', () => ({
        ...defaults,
        title: asset?.chart?.name ??
            $t({
                defaultMessage: 'Chart',
                description: 'Fallback title for an unnamed chart asset',
                id: 'abce182ab8',
            }),
        Icon: $icon('chart-histogram'),
    }))
        .with('CODE_FILE', () => {
        const ext = getFileExtension(filename);
        const extDescription = getLocalizedCodeFileDescription(ext, $t);
        return {
            ...defaults,
            description: extDescription ?? defaults.description,
            title: asset?.code_file?.name ?? asset?.code_file?.filename ?? '',
            Icon: getIconFromFilename(filename) ?? getIconForFileType(extension),
        };
    })
        .with('ASSET_DIFF', () => {
        const ext = getFileExtension(filename);
        const extDescription = getLocalizedCodeFileDescription(ext, $t);
        return {
            ...defaults,
            description: extDescription ?? defaults.description,
            title: asset?.asset_diff?.title ?? filename,
            Icon: getIconFromFilename(filename) ?? getIconForFileType(extension, $icon('code')),
        };
    })
        .with('APP', () => ({
        ...defaults,
        title: asset?.app?.name ?? '',
        Icon: $icon('layout-collage'),
        isStreaming: !asset?.app?.final,
    }))
        .with('SLIDES', () => ({
        ...defaults,
        title: asset?.slide_file?.name ?? asset?.app?.name ?? '',
        Icon: $icon('presentation'),
        isStreaming: asset?.app != null && !asset.app.final,
    }))
        .with('GENERATED_IMAGE', () => ({
        ...defaults,
        title: getAssetDownloadFilename(asset) ?? getLocalizedImageTitle(asset?.generated_image?.type, $t),
        Icon: asset?.generated_image?.type === 'MEDIA_TYPE_CHART'
            ? $icon('chart-histogram')
            : $icon('photo'),
        previewUrl: defaults.previewUrl,
    }))
        .with('GENERATED_VIDEO', () => ({
        ...defaults,
        title: getAssetDownloadFilename(asset) ?? getLocalizedVideoTitle(asset?.generated_video?.type, $t),
        Icon: $icon('video'),
    }))
        .with('AUDIO_FILE', () => ({
        ...defaults,
        title: asset?.audio_file?.name ??
            $t({
                defaultMessage: 'Audio',
                id: '1c78995a29',
                description: 'Fallback title for an unnamed audio file asset',
            }),
        Icon: $icon('file-music'),
    }))
        .with('MODEL_3D', () => ({
        ...defaults,
        title: asset?.model_3d?.name ??
            asset?.model_3d?.filename ??
            $t({
                defaultMessage: '3D Model',
                id: 'cc01d04ce0',
                description: 'Fallback title for an unnamed 3D model asset',
            }),
        description: $t({
            defaultMessage: '3D Model',
            id: '29cae9f7fa',
            description: 'Descriptor text for a 3D model asset',
        }),
        Icon: $icon('box'),
    }))
        .with('PDF_FILE', () => ({
        ...defaults,
        title: asset?.pdf_file?.name ??
            $t({
                defaultMessage: 'PDF Document',
                description: 'Fallback title for an unnamed PDF file asset',
                id: '07238f787a',
            }),
        Icon: $icon('file-type-pdf'),
    }))
        .with('DOCX_FILE', () => ({
        ...defaults,
        title: asset?.docx_file?.name ?? '',
        Icon: $icon('file-type-docx'),
    }))
        .with('DOC_FILE', () => ({
        ...defaults,
        title: asset?.doc_file?.name ?? '',
        Icon: $icon('file-text'),
        isStreaming: !asset?.doc_file?.final,
    }))
        .with('RESEARCH_REPORT', () => ({
        ...defaults,
        title: asset?.research_report?.name ??
            $t({
                defaultMessage: 'Research Report',
                description: 'Fallback title for an unnamed research report asset',
                id: 'bfa5a5ebbc',
            }),
        Icon: $icon('file-text'),
        isStreaming: !asset?.research_report?.final,
    }))
        .with('XLSX_FILE', () => ({
        ...defaults,
        title: asset?.xlsx_file?.name ?? '',
        Icon: $icon('file-spreadsheet'),
        isStreaming: !asset?.xlsx_file?.final,
    }))
        .with('QUIZ', () => ({
        ...defaults,
        title: asset?.quiz?.title ?? '',
        Icon: $icon('checklist'),
    }))
        .with('FLASHCARDS', () => ({
        ...defaults,
        title: asset?.flashcards?.title ?? '',
        Icon: $icon('cards'),
    }))
        .with('SCREENSHOT', () => ({
        ...defaults,
        description: '',
        title: $t({
            defaultMessage: 'Screenshot',
            id: 'ec645950c5',
            description: 'Title for a screenshot asset in the chain-of-thought',
        }),
        Icon: $icon('camera'),
        previewUrl: defaults.previewUrl,
    }))
        .with('EXTERNAL_URL', () => ({
        ...defaults,
        title: getExternalUrlLabel(asset?.external_url?.url, asset?.external_url?.title),
        Icon: asset?.external_url?.automation ? AUTOMATIONS_ICON : $icon('external-link'),
    }))
        .with('AUTOMATION', () => ({
        ...defaults,
        title: asset?.automation?.title?.trim() ||
            $t({
                defaultMessage: 'Automation',
                id: 'f173e22dd1',
                description: 'Fallback title for an automation asset',
            }),
        Icon: AUTOMATIONS_ICON,
    }))
        .with('TEAMMATE_TASK', () => defaults)
        .with('PULL_REQUEST', () => ({
        ...defaults,
        description: $t({
            defaultMessage: 'Pull request',
            id: 'dbb8c8095b',
            description: 'Asset type descriptor for a pull request',
        }),
        title: asset?.pull_request?.title?.trim() ||
            $t({
                defaultMessage: 'Pull request',
                id: '0c70c3ead0',
                description: 'Fallback title for a pull request asset',
            }),
        Icon: $icon('brand-github'),
    }))
        .with('SESSION', () => ({
        ...defaults,
        description: $t({
            defaultMessage: 'Session',
            id: '3df113b2d7',
            description: 'Asset type descriptor for a shared Perplexity session',
        }),
        title: asset?.session?.title?.trim() ||
            $t({
                defaultMessage: 'Session',
                id: '8034f1868c',
                description: 'Fallback title for a shared Perplexity session asset',
            }),
        Icon: $icon('messages'),
    }))
        .with('TEAMMATE_WORKER', () => ({
        ...defaults,
        description: $t({
            defaultMessage: 'Agent',
            id: 'cbe810f11e',
            description: 'Asset type descriptor for a durable Teammate coding worker',
        }),
        title: teammateWorkerTitle ||
            $t({
                defaultMessage: 'Agent',
                id: '75f18278ca',
                description: 'Fallback title for a durable Teammate coding worker asset',
            }),
        Icon: $icon('code'),
    }))
        .with('VISUALIZATION', () => ({
        ...defaults,
        title: asset?.visualization?.name ??
            $t({
                defaultMessage: 'Visualization',
                description: 'Fallback title for an unnamed visualization asset',
                id: 'b1f96ec9c6',
            }),
        Icon: $icon('layout-collage'),
    }))
        .otherwise(() => ({
        ...defaults,
        description: defaults.description,
        title: getAssetDownloadFilename(asset) ??
            $t({
                defaultMessage: 'Generated File',
                description: 'Fallback title for an unrecognized asset type',
                id: 'fc994c6c19',
            }),
        Icon: getIconFromFilename(filename) ?? getIconForFileType(extension) ?? $icon('file'),
    }));
    return baseInfo;
};
/**
 * Returns a human-readable description for an asset type.
 * Unlike `useAssetInfo`, this only needs the `asset_type` value and does not
 * require the full protobuf Asset shape — making it safe to use with the
 * flat REST API Asset type from `useAssetsQuery`.
 *
 * Accepts the `AssetType` enum for autocomplete, but also plain strings
 * via `(string & {})` for compatibility with REST API types.
 */
export function useAssetTypeDescription(assetType, mediaType) {
    const { $t } = useIntl();
    return match(assetType)
        .with('CODE_ASSET', () => $t({
        defaultMessage: 'Code',
        description: 'Asset type descriptor for a code asset in the chain-of-thought and assets tab',
        id: '073fe5c19c',
    }))
        .with('CHART', () => $t({
        defaultMessage: 'Chart',
        description: 'Asset type descriptor for a chart asset in the chain-of-thought and assets tab',
        id: '7d5dd72ab7',
    }))
        .with('CODE_FILE', () => $t({
        defaultMessage: 'Generated File',
        description: 'Fallback asset type descriptor for a code file with an unrecognized extension',
        id: '59e09063c6',
    }))
        .with('APP', () => $t({
        defaultMessage: 'App',
        description: 'Asset type descriptor for an app asset in the chain-of-thought and assets tab',
        id: '0c6f43e7d5',
    }))
        .with('SLIDES', () => $t({
        defaultMessage: 'Presentation',
        description: 'Asset type descriptor for a slides/presentation asset in the chain-of-thought and assets tab',
        id: '5b72cf0ad8',
    }))
        .with('GENERATED_IMAGE', () => getLocalizedImageTitle(mediaType, $t))
        .with('GENERATED_VIDEO', () => getLocalizedVideoTitle(mediaType, $t))
        .with('AUDIO_FILE', () => $t({
        defaultMessage: 'Audio File',
        id: '73c4546121',
        description: 'Asset type descriptor for an audio file in the chain-of-thought and assets tab',
    }))
        .with('MODEL_3D', () => $t({
        defaultMessage: '3D Model',
        id: '3b834bd3ba',
        description: 'Asset type descriptor for a 3D model asset in the chain-of-thought and assets tab',
    }))
        .with('PDF_FILE', () => $t({
        defaultMessage: 'PDF Document',
        description: 'Asset type descriptor for a PDF file in the chain-of-thought and assets tab',
        id: 'c3c28860cc',
    }))
        .with('DOCX_FILE', () => $t({
        defaultMessage: 'Word Document',
        description: 'Asset type descriptor for a Word document in the chain-of-thought and assets tab',
        id: '7db9bb30d9',
    }))
        .with('DOC_FILE', () => $t({
        defaultMessage: 'Document',
        description: 'Asset type descriptor for a generic document file in the chain-of-thought and assets tab',
        id: 'cf37877cbf',
    }))
        .with('RESEARCH_REPORT', () => $t({
        defaultMessage: 'Deep research report',
        description: 'Asset type descriptor for a research report asset in the chain-of-thought and assets tab',
        id: '1763f96902',
    }))
        .with('XLSX_FILE', () => $t({
        defaultMessage: 'Excel Spreadsheet',
        description: 'Asset type descriptor for an Excel spreadsheet asset in the chain-of-thought and assets tab',
        id: 'eb0a291f98',
    }))
        .with('QUIZ', () => $t({
        defaultMessage: 'Quiz',
        description: 'Asset type descriptor for a quiz asset in the chain-of-thought and assets tab',
        id: 'b9f228ce47',
    }))
        .with('FLASHCARDS', () => $t({
        defaultMessage: 'Flashcards',
        description: 'Asset type descriptor for a flashcards asset in the chain-of-thought and assets tab',
        id: '23c3b99bf2',
    }))
        .with('EXTERNAL_URL', () => $t({
        defaultMessage: 'Link',
        description: 'Asset type descriptor for an external URL/link asset in the chain-of-thought and assets tab',
        id: '3ca78b4796',
    }))
        .with('AUTOMATION', () => $t({
        defaultMessage: 'Automation',
        description: 'Asset type descriptor for an automation asset',
        id: 'edd92736ca',
    }))
        .with('VISUALIZATION', () => $t({
        defaultMessage: 'Visualization',
        description: 'Asset type descriptor for an interactive visualization',
        id: '9a6a524df6',
    }))
        .with('SCREENSHOT', () => '')
        .otherwise(() => '');
}
//# sourceMappingURL=useAssetInfo.js.map